import { SB } from '@play-co/replicant';
import createScheduledActions, {
  extractScheduledActionHandlers,
} from '../config/scheduledActions/createScheduledActions';
import { deliverChatbotMessage } from '../chatbot/chatbot.util';
import { getChatbotFlexMessage } from '../chatbot/flex/utils';
import { generateReplicantChatbotPayload } from '../chatbot/messageTemplates';
import * as Pet from '../pet';
import { Locale } from '../ruleset/locale';
import { Feature } from '../analytics';
import { getLocale } from '../getters/user';
import { isOnlineOrIdle } from '../getters/presence';
import { isGuestUser } from '../guest/guest.getters';
import {
  buddiesGetEvent,
  buddiesIsAvailable,
  buddiesIsPartyFull,
} from './buddies.getters';

const schema = {
  sendInviteBuddyOA: SB.object({}),
  sendBuddiesNudge: SB.object({
    senderId: SB.string(),
    senderName: SB.string(),
    senderPhoto: SB.string(),
  }),
};

const actions = createScheduledActions(schema)({
  sendInviteBuddyOA: async (state, _, api) => {
    // Player is online or idle. Bail.
    if (isOnlineOrIdle(api)) return;

    // Notifications are off. Bail.
    if (state.settings.receiveMessages === 'off') return;

    // Use doesn't have buddies enabled. Bail.
    const now = api.date.now();
    if (!buddiesIsAvailable(state, now)) return;

    // Party already full. Bail.
    if (buddiesIsPartyFull(state, now)) return;

    // Render the chatbot message.
    const species = Pet.getters.getCurrentEvolution(state)?.species ?? 'dog';
    const message = getChatbotFlexMessage({
      template: 'buddiesInvitation',
      payload: {
        ...generateReplicantChatbotPayload({
          feature: Feature.BUDDIES._,
          $subFeature: Feature.BUDDIES.INVITE_OA,
        }),
        $deepLink: { type: 'popup', id: 'popupBuddiesInvite', args: {} },
      },
      data: {
        locale: state.settings.locale as Locale,
        petName: state.pet.name,
        species,
      },
    });

    // Deliver the chatbot message forcing OA.
    deliverChatbotMessage(state, api, {
      receiverId: state.id,
      message,
      channel: 'oa',
    });
  },

  sendBuddiesNudge: async (state, args, api) => {
    // Buddies not available for this user.
    const now = api.date.now();
    if (!buddiesIsAvailable(state, now)) return;

    // Sender must still be in recipient's party.
    const event = buddiesGetEvent(state, now);
    if (!event?.party[args.senderId]) return;

    const receiverId = state.id;
    const channel = isGuestUser({ id: receiverId }) ? 'push' : 'oa';

    const message = getChatbotFlexMessage({
      template: 'buddiesNudge',
      payload: {
        ...generateReplicantChatbotPayload({
          feature: Feature.BUDDIES._,
          $subFeature: Feature.BUDDIES.NUDGE_OA,
        }),
      },
      data: {
        senderName: args.senderName,
        senderPhoto: args.senderPhoto,
        locale: getLocale(state),
      },
    });

    deliverChatbotMessage(state, api, { receiverId, message, channel });
  },
});

export const buddiesScheduledActions = {
  schema,
  actions: extractScheduledActionHandlers(actions),
};
